import ProviderAPIClient from '@/features/auth/ProviderAPIClient'
import { EpicAuthClient, EPIC_FHIR_SESSIONS_UPDATED_EVENT } from '@/features/auth/services/EpicAuthClient'
import { useAuth0 } from '@auth0/auth0-react'
import * as Sentry from '@sentry/react'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import {
  isEpicUiConfigLoadInProgress,
  setEpicUiConfigLoadInProgress,
} from './loadEpicUiConfig'
import {
  applyThemeTokens,
  assertUiConfig,
  FALLBACK_UI_CONFIG_ID,
  getActiveUiConfig,
  InvalidUiConfigError,
  LOGIN_FALLBACK_UI_CONFIG,
  mergeClientNameFromApiEnvelope,
  setActiveUiConfig,
  type UiConfig,
} from './uiConfig'
import { UiConfigContext, type UiConfigState } from './uiConfigContext'

const IMDB_ORG_ID_CLAIM = 'https://inflectionmedicine.com/org_id'

/** Decode IMDB org UUID from a provider-api (epic-provision) JWT payload. */
function imdbOrgIdFromProviderJwt(jwt: string | null | undefined): string | undefined {
  if (!jwt?.trim()) {
    return undefined
  }
  try {
    const parts = jwt.split('.')
    if (parts.length < 2) {
      return undefined
    }
    const payload = JSON.parse(atob(parts[1]!)) as Record<string, unknown>
    const raw = payload[IMDB_ORG_ID_CLAIM]
    return typeof raw === 'string' && raw.trim() ? raw.trim() : undefined
  } catch {
    return undefined
  }
}

const resolveOrganizationContext = (): {
  organizationId: string | null
  jwtToken: string | null
  apiKey: string | null // Backward compatibility
} => {
  const authClient = EpicAuthClient.getInstance()
  const instanceState = authClient.getInstanceState()
  const organizationId = authClient.getOrgId(instanceState)
  const jwtToken = authClient.getJwtToken(instanceState)
  const context = authClient.getContext(instanceState)
  return {
    organizationId,
    jwtToken,
    apiKey: context.apiKey, // Backward compatibility
  }
}

// Public routes that should use fallback config immediately
const PUBLIC_ROUTES = ['/login', '/login/callback', '/verify-email', '/sign', '/health']

const isPublicRoute = (pathname: string): boolean => {
  return PUBLIC_ROUTES.some((route) => pathname === route || pathname.startsWith(`${route}/`))
}

// Auth-handshake routes that must render even while UI config is still loading.
// These routes (Launch, Epic callback, Auth0 callback) are what *produce* the
// JWT the config loader waits on — if we hide them behind the loading overlay
// we deadlock: no children render → no token exchange → JWT never arrives.
// ``/`` is the app entry: it only <Navigate to="/login" />; if we block
// on loading here, the navigate never mounts and the user stays on ``/`` forever
// (stuck on "Loading client configuration...").
// ``/login`` and ``/verify-email`` must also bypass the overlay: Auth0's first
// paint can race with the config effect (silent auth / iframe); blocking the
// route tree until ``status: 'ready'`` can strand users on the loading card
// until a full refresh reorders timing.
const AUTH_BOOTSTRAP_ROUTES = [
  '/',
  '/launch',
  '/epic-sandbox/callback',
  '/login/callback',
  '/login',
  '/verify-email',
  '/sign',
  '/health',
]

const isAuthBootstrapRoute = (pathname: string): boolean => {
  return AUTH_BOOTSTRAP_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`)
  )
}

/**
 * Apply a fully-formed ``UiConfig`` to theme + active singleton in one place
 * so every code path stays consistent (no merging, no defaults injection).
 */
const applyConfig = (config: UiConfig): void => {
  applyThemeTokens(config.themeTokens)
  setActiveUiConfig(config)
}

export function UiConfigProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading: auth0Loading, getIdTokenClaims, getAccessTokenSilently, logout } = useAuth0()
  const location = useLocation()
  const [state, setState] = useState<UiConfigState>({
    config: LOGIN_FALLBACK_UI_CONFIG,
    status: 'loading',
  })
  /** Bumps when EpicAuthClient persists sessions so the config effect can retry after EpicFhirAccessToken. */
  const [epicSessionRevision, setEpicSessionRevision] = useState(0)

  // Auth0 hook functions are not referentially stable; keep refs so config load does not loop every render.
  const getAccessTokenSilentlyRef = useRef(getAccessTokenSilently)
  const getIdTokenClaimsRef = useRef(getIdTokenClaims)
  getAccessTokenSilentlyRef.current = getAccessTokenSilently
  getIdTokenClaimsRef.current = getIdTokenClaims

  // Single ref to track if we've attempted to load config
  const hasAttemptedLoadRef = useRef(false)

  // Initialize ProviderAPIClient once; token getter always reads latest Auth0 fn from ref.
  useEffect(() => {
    ProviderAPIClient.getInstance(() => getAccessTokenSilentlyRef.current())
    console.log('[UiConfigProvider] ProviderAPIClient initialized with JWT authentication')
  }, [])

  useEffect(() => {
    const onEpicSessionsPersisted = () => {
      setEpicSessionRevision((n) => n + 1)
    }
    window.addEventListener(EPIC_FHIR_SESSIONS_UPDATED_EVENT, onEpicSessionsPersisted)
    return () => {
      window.removeEventListener(EPIC_FHIR_SESSIONS_UPDATED_EVENT, onEpicSessionsPersisted)
    }
  }, [])

  // Epic JWT is set on the in-memory session before localStorage save; poll so we are not blocked on the save event only.
  useEffect(() => {
    if (state.status !== 'loading') {
      return
    }
    const { organizationId: oid, jwtToken: j, apiKey: k } = resolveOrganizationContext()
    if (!oid || j || k) {
      return
    }
    const id = window.setInterval(() => {
      const ctx = resolveOrganizationContext()
      if (ctx.jwtToken || ctx.apiKey) {
        setEpicSessionRevision((n) => n + 1)
      }
    }, 500)
    return () => window.clearInterval(id)
  }, [state.status, epicSessionRevision])

  useEffect(() => {
    ProviderAPIClient.getInstance()

    // If state is loading, try to restore from stored config immediately
    // This handles the case where config was loaded but state got reset
    if (state.status === 'loading') {
      const storedConfig = getActiveUiConfig()
      const shouldRestoreStored =
        storedConfig != null &&
        (storedConfig.id !== FALLBACK_UI_CONFIG_ID || hasAttemptedLoadRef.current)

      if (shouldRestoreStored) {
        console.log('[UiConfigProvider] State is loading but config exists, restoring immediately')
        setState({
          config: storedConfig,
          status: 'ready',
        })
        hasAttemptedLoadRef.current = true
        return // Config already loaded, no need to reload
      }
    }

    // If we've already attempted to load, don't reload
    if (hasAttemptedLoadRef.current) {
      return
    }

    // Mark that we're attempting to load. ``hasAttemptedLoadRef`` guards
    // against duplicate fetches when the effect re-runs (auth state changes,
    // location changes), so the single in-flight load is allowed to complete
    // its setState even after the effect cleanup — without this, an Auth0
    // ``isAuthenticated`` flip mid-fetch would orphan the response and leave
    // the provider stuck on ``status: 'loading'``.
    hasAttemptedLoadRef.current = true

    const loadConfig = async () => {
      try {
        // Public routes (e.g. /login) must not run the Epic/Auth0 config fetches first: a stale
        // partial Epic session can otherwise call /config/ui without a provider JWT and get 401
        // (provider-api requires Bearer or x-api-key for that route in non-local envs).
        if (isPublicRoute(location.pathname)) {
          console.log('[UiConfigProvider] Public route — login fallback only (skipping /config/ui)')
          applyConfig(LOGIN_FALLBACK_UI_CONFIG)
          setState({ config: LOGIN_FALLBACK_UI_CONFIG, status: 'ready' })
          hasAttemptedLoadRef.current = false
          return
        }

        // Step 1: Check Epic flow first
        const { organizationId: epicOrgId, jwtToken, apiKey } = resolveOrganizationContext()

        // Epic session exists but JWT not yet available (EpicFhirAccessToken / epic-provision not complete).
        // Don't mark attempt as done so we retry when effect runs again (e.g. after callback navigates).
        if (epicOrgId && !(jwtToken || apiKey)) {
          console.log(
            '[UiConfigProvider] Epic flow: waiting for JWT (EpicFhirAccessToken not yet complete), will retry on next run'
          )
          hasAttemptedLoadRef.current = false
          return
        }

        if (epicOrgId && (jwtToken || apiKey)) {
          console.log('[UiConfigProvider] Epic flow detected, orgId:', epicOrgId)
          const client = ProviderAPIClient.getInstance()
          const epicAuth = EpicAuthClient.getInstance()
          const epicState = epicAuth.getInstanceState()

          // Prefer JWT, fallback to API key for backward compatibility
          if (jwtToken) {
            console.log('[UiConfigProvider] Using JWT token for Epic OAuth flow')
            // Avoid race with Epic callback (loadEpicUiConfig): if config already set, sync state and skip fetch.
            const existingConfig = getActiveUiConfig()
            if (existingConfig && existingConfig.id !== FALLBACK_UI_CONFIG_ID) {
              setState({ config: existingConfig, status: 'ready' })
              return
            }
            // If callback (or another run) is already loading Epic config, don't start a second fetch.
            if (isEpicUiConfigLoadInProgress()) {
              hasAttemptedLoadRef.current = false
              return
            }

            setEpicUiConfigLoadInProgress(true)
            try {
              // Do not override ProviderAPIClient's token getter with the raw Epic JWT — getAuthToken()
              // already prefers the epic-provision (Auth0-compatible) JWT when a session exists.
              const providerJwt = await epicAuth.getOrRefreshProviderApiToken(epicState)
              const uiConfigPathOrgId =
                epicAuth.getImdbOrganizationId(epicState)?.trim() || imdbOrgIdFromProviderJwt(providerJwt)
              const fhirBase = epicAuth.getFhirServerUrl(epicState)?.trim()
              const fhirQuery =
                fhirBase && fhirBase.length > 0
                  ? `?fhirServerUrl=${encodeURIComponent(fhirBase)}`
                  : ''
              if (!uiConfigPathOrgId) {
                console.error(
                  '[UiConfigProvider] Epic flow: IMDB org id missing after epic-provision (expected claim',
                  IMDB_ORG_ID_CLAIM,
                  '). Cannot call /config/ui.'
                )
                applyConfig(LOGIN_FALLBACK_UI_CONFIG)
                setState({
                  config: LOGIN_FALLBACK_UI_CONFIG,
                  status: 'error',
                  error: 'Epic session is missing IMDB organization id for UI config',
                })
                return
              }

              const response = await client.get(
                `/config/ui/${encodeURIComponent(uiConfigPathOrgId)}${fhirQuery}`,
                {
                  Accept: 'application/json',
                }
              )

              if (!response.ok) {
                applyConfig(LOGIN_FALLBACK_UI_CONFIG)
                setState({
                  config: LOGIN_FALLBACK_UI_CONFIG,
                  status: 'error',
                  error: `Failed to load UI config: ${response.status}`,
                })
                return
              }

              const responseData = (await response.json()) as { ui_config?: unknown; client_name?: string | null }
              try {
                const config = mergeClientNameFromApiEnvelope(
                  assertUiConfig(responseData.ui_config),
                  responseData.client_name
                )
                applyConfig(config)
                setState({ config, status: 'ready' })
                console.log('[UiConfigProvider] Epic config loaded successfully with JWT')
              } catch (validationError) {
                const message =
                  validationError instanceof InvalidUiConfigError
                    ? validationError.message
                    : 'Invalid UI config payload'
                console.error('[UiConfigProvider] Epic JWT: invalid /config/ui payload:', message)
                applyConfig(LOGIN_FALLBACK_UI_CONFIG)
                setState({ config: LOGIN_FALLBACK_UI_CONFIG, status: 'error', error: message })
              }
              return
            } finally {
              setEpicUiConfigLoadInProgress(false)
            }
          } else if (apiKey) {
            console.log('[UiConfigProvider] Using API key for Epic OAuth flow (backward compatibility)')
            const uiConfigPathOrgId =
              epicAuth.getImdbOrganizationId(epicState)?.trim() || epicOrgId
            const fhirBaseApiKey = epicAuth.getFhirServerUrl(epicState)?.trim()
            const fhirQueryApiKey =
              fhirBaseApiKey && fhirBaseApiKey.length > 0
                ? `?fhirServerUrl=${encodeURIComponent(fhirBaseApiKey)}`
                : ''
            if (!epicAuth.getImdbOrganizationId(epicState)?.trim()) {
              console.warn(
                '[UiConfigProvider] Epic API-key path: no IMDB org id on session; using Epic org id for /config/ui (likely 404). Add claim',
                IMDB_ORG_ID_CLAIM,
                'to epic-provision JWT.'
              )
            }
            const response = await fetch(
              `${import.meta.env.VITE_PROVIDER_API_URL}/config/ui/${encodeURIComponent(uiConfigPathOrgId)}${fhirQueryApiKey}`,
              {
                method: 'GET',
                headers: {
                  Accept: 'application/json',
                  'x-api-key': apiKey,
                },
              }
            )

            if (!response.ok) {
              applyConfig(LOGIN_FALLBACK_UI_CONFIG)
              setState({
                config: LOGIN_FALLBACK_UI_CONFIG,
                status: 'error',
                error: `Failed to load UI config: ${response.status}`,
              })
              return
            }

            const responseData = (await response.json()) as { ui_config?: unknown; client_name?: string | null }
            try {
              const config = mergeClientNameFromApiEnvelope(
                assertUiConfig(responseData.ui_config),
                responseData.client_name
              )
              applyConfig(config)
              setState({ config, status: 'ready' })
              console.log('[UiConfigProvider] Epic config loaded successfully with API key')
            } catch (validationError) {
              const message =
                validationError instanceof InvalidUiConfigError
                  ? validationError.message
                  : 'Invalid UI config payload'
              console.error('[UiConfigProvider] Epic API key: invalid /config/ui payload:', message)
              applyConfig(LOGIN_FALLBACK_UI_CONFIG)
              setState({ config: LOGIN_FALLBACK_UI_CONFIG, status: 'error', error: message })
            }
            return
          }
        }

        // Step 2: Check Auth0 standalone flow
        // Try to get claims regardless of loading state - Auth0 might have cached token
        // Only wait if we can't get claims AND Auth0 is still loading AND we're not authenticated
        console.log(
          '[UiConfigProvider] Attempting to get token claims (auth0Loading:',
          auth0Loading,
          ', isAuthenticated:',
          isAuthenticated,
          ')'
        )

        try {
          // Try to get claims - this works even if auth0Loading is true (cached token)
          // Wrap in try-catch to handle Auth0Provider errors gracefully
          let claims: Awaited<ReturnType<typeof getIdTokenClaimsRef.current>>
          try {
            claims = await getIdTokenClaimsRef.current()
          } catch (claimsError) {
            if (claimsError instanceof Error && claimsError.message.includes('Auth0Provider')) {
              console.error('[UiConfigProvider] Auth0Provider not available:', claimsError.message)
              Sentry.captureException(claimsError, {
                tags: {
                  operation: 'ui_config.get_claims',
                  flow: 'standalone',
                  error_type: 'auth0_provider_missing',
                },
              })
              applyConfig(LOGIN_FALLBACK_UI_CONFIG)
              setState({ config: LOGIN_FALLBACK_UI_CONFIG, status: 'ready' })
              hasAttemptedLoadRef.current = false
              return
            }
            throw claimsError
          }

          // If we got claims, proceed regardless of loading state
          if (claims) {
            // Token claims contain PII/PHI (email, name, NPI, org metadata), so
            // never log their values. In dev only, log claim *key names* to aid
            // debugging organization/custom-claim mapping.
            if (import.meta.env.DEV) {
              console.log('[UiConfigProvider] Retrieved claims; keys:', Object.keys(claims))
              const customClaimKeys = Object.keys(claims).filter((key) => key.startsWith('https://'))
              if (customClaimKeys.length > 0) {
                console.log('[UiConfigProvider] Custom namespaced claim keys:', customClaimKeys)
              }
            }
          } else if (auth0Loading && !isAuthenticated) {
            // No claims, Auth0 still loading, and not authenticated - wait
            console.log('[UiConfigProvider] No claims, Auth0 still loading, and not authenticated - will retry')
            hasAttemptedLoadRef.current = false
            return
          }

          if (!claims && !isAuthenticated) {
            console.log('[UiConfigProvider] No token claims available and not authenticated, using login fallback')
            applyConfig(LOGIN_FALLBACK_UI_CONFIG)
            setState({ config: LOGIN_FALLBACK_UI_CONFIG, status: 'ready' })
            // When the user signs in (same or next effect), allow the Auth0 /config/ui path to run instead
            // of bailing on ``hasAttemptedLoadRef`` from this placeholder load.
            hasAttemptedLoadRef.current = false
            return
          }

          // Stale cached claims can outlive a dead session: with
          // ``cacheLocation: 'localstorage'`` the id-token claims persist even
          // after the Auth0 session is gone, so ``claims`` can be truthy while
          // the user is not actually authenticated (e.g. a deep link from an
          // email opened in a logged-out browser). We must NOT call the
          // token-protected ``/config/ui`` in that state: ``getAccessTokenSilently``
          // (``useRefreshTokens: false``) would block on a silent-auth iframe
          // and hang the "Loading client configuration..." overlay until Auth0's
          // ~60s timeout. Defer while Auth0 is still settling; otherwise apply the
          // login fallback and let the route guard (SecureEHRRoute) redirect to /login.
          if (!isAuthenticated) {
            if (auth0Loading) {
              console.log(
                '[UiConfigProvider] Cached claims present but Auth0 not authenticated yet (still loading) - will retry'
              )
              hasAttemptedLoadRef.current = false
              return
            }
            console.log(
              '[UiConfigProvider] Cached claims present but not authenticated; using login fallback so route guard can redirect to /login'
            )
            applyConfig(LOGIN_FALLBACK_UI_CONFIG)
            setState({ config: LOGIN_FALLBACK_UI_CONFIG, status: 'ready' })
            hasAttemptedLoadRef.current = false
            return
          }

          // Try multiple possible locations for organization metadata
          // 1. Custom namespaced claim (requires Auth0 Action to add it)
          const clientConfigKey = claims?.['https://provider.dev.inflectionmedicine.com/clientConfigKey'] as
            | string
            | undefined

          // 2. Check for organization metadata in other possible locations
          const claimsRecord = claims as Record<string, unknown>
          const orgMetadata = claimsRecord?.['https://provider.dev.inflectionmedicine.com/org_metadata'] as
            | Record<string, unknown>
            | undefined
          const orgId = claimsRecord?.org_id as string | undefined

          // Log what we found for debugging. Claim values are PII/PHI (org id and
          // org metadata), so never log their values; in dev only, log presence and
          // key names to aid organization/custom-claim mapping debugging.
          if (!clientConfigKey) {
            console.warn('[UiConfigProvider] clientConfigKey not found in claims')
            if (import.meta.env.DEV) {
              console.log('[UiConfigProvider] Available claim keys:', Object.keys(claims || {}))
              console.log('[UiConfigProvider] org_id claim present:', Boolean(orgId))
              if (orgMetadata) {
                console.log('[UiConfigProvider] org_metadata claim keys:', Object.keys(orgMetadata))
              }
            }
          }

          // Use clientConfigKey from custom claim, or try to get it from organization metadata
          const resolvedClientConfigKey = clientConfigKey || orgMetadata?.clientConfigKey
          const imdbOrgIdFromClaim = (claimsRecord?.[IMDB_ORG_ID_CLAIM] as string | undefined)?.trim()
          // provider-api /config/ui/{id} is IMDB-org-UUID keyed (microapp); never use Epic clientConfigKey here.
          const configPathParam = imdbOrgIdFromClaim
          if (!configPathParam && resolvedClientConfigKey) {
            // Don't log the resolved key value (claim-derived); the message is enough.
            console.warn(
              '[UiConfigProvider] Standalone: `https://inflectionmedicine.com/org_id` claim missing; ' +
                'not falling back to clientConfigKey (would 404). Add the IMDB org UUID claim via Auth0 Action.'
            )
          }

          if (configPathParam) {
            if (import.meta.env.DEV) {
              console.log('[UiConfigProvider] Auth0 standalone flow (JWT-only) for', configPathParam)
            }
            const client = ProviderAPIClient.getInstance()

            // Confirm the access token is obtainable before firing the authenticated
            // request. If the Auth0 session can no longer mint a token, this throws and
            // we must NOT call /config/ui — a headerless request is what the backend
            // rejects with 401 (the bug this guards against).
            try {
              await getAccessTokenSilentlyRef.current()
            } catch (tokenError) {
              // Force a local logout so the route guard redirects the user to /login.
              //
              // Why this is required (and not redundant with the SDK): auth0-spa-js only
              // auto-calls logout() for the `login_required` error. For `Timeout` (dead
              // session via the silent-auth iframe, the common case with
              // useRefreshTokens: false) and `consent_required`, it does NOT clear the
              // cache, so getUser() still returns the stale cached user and
              // isAuthenticated stays `true` — meaning StandaloneAuthGuard never
              // redirects. Calling logout() here clears the cache and flips
              // isAuthenticated to false for ALL of those cases, uniformly.
              //
              // openUrl: false keeps this local (no round-trip to Auth0's /logout); we
              // only want isAuthenticated to flip so the in-app guard handles the redirect.
              // Calling it when the SDK already logged out (login_required) is a safe no-op.
              console.warn('[UiConfigProvider] Could not get access token for /config/ui, logging out so the route guard redirects to /login:', tokenError)
              logout({ openUrl: false })

              // logout() flips isAuthenticated asynchronously (React re-render), so the
              // redirect is not instantaneous. Apply the login fallback now so the user
              // never gets stranded on the "Loading client configuration..." overlay during
              // that brief gap; reset the attempt flag so config can reload if the user
              // signs back in without a full page reload.
              applyConfig(LOGIN_FALLBACK_UI_CONFIG)
              setState({ config: LOGIN_FALLBACK_UI_CONFIG, status: 'ready' })
              hasAttemptedLoadRef.current = false
              return
            }

            const response = await client.get(
              `/config/ui/${configPathParam}`,
              { Accept: 'application/json' },
              true // withAuth=true: Auto-inject JWT Bearer token
            )

            console.log('[UiConfigProvider] /config/ui response status:', response.status)

            if (!response.ok) {
              const errorText = await response.text().catch(() => 'Unknown error')
              console.error('[UiConfigProvider] Failed to fetch /config/ui:', response.status, errorText)
              applyConfig(LOGIN_FALLBACK_UI_CONFIG)
              setState({
                config: LOGIN_FALLBACK_UI_CONFIG,
                status: 'error',
                error: `Failed to load UI config: ${response.status}`,
              })
              return
            }

            const responseData = (await response.json()) as { ui_config?: unknown; client_name?: string | null }
            try {
              const config = mergeClientNameFromApiEnvelope(
                assertUiConfig(responseData.ui_config),
                responseData.client_name
              )
              applyConfig(config)
              setState({ config, status: 'ready' })
              console.log('[UiConfigProvider] Auth0 standalone config loaded successfully')
            } catch (validationError) {
              const message =
                validationError instanceof InvalidUiConfigError
                  ? validationError.message
                  : 'Invalid UI config payload'
              console.error('[UiConfigProvider] Auth0 standalone: invalid /config/ui payload:', message)
              applyConfig(LOGIN_FALLBACK_UI_CONFIG)
              setState({ config: LOGIN_FALLBACK_UI_CONFIG, status: 'error', error: message })
            }
            return
          }

          // No IMDB org_id in claims — cannot load a real config; fail loud.
          const message =
            'Authenticated session is missing the IMDB org_id claim required to load /config/ui'
          console.error('[UiConfigProvider]', message)
          applyConfig(LOGIN_FALLBACK_UI_CONFIG)
          setState({ config: LOGIN_FALLBACK_UI_CONFIG, status: 'error', error: message })
          return
        } catch (claimsError) {
          Sentry.captureException(claimsError, {
            tags: {
              operation: 'ui_config.get_claims',
              flow: 'standalone',
            },
          })
          console.error('[UiConfigProvider] Error getting claims:', claimsError)
          applyConfig(LOGIN_FALLBACK_UI_CONFIG)
          setState({
            config: LOGIN_FALLBACK_UI_CONFIG,
            status: 'error',
            error: claimsError instanceof Error ? claimsError.message : 'Failed to read auth claims',
          })
          return
        }
      } catch (error) {
        Sentry.captureException(error, {
          tags: {
            operation: 'ui_config.load',
          },
        })
        applyConfig(LOGIN_FALLBACK_UI_CONFIG)
        setState({
          config: LOGIN_FALLBACK_UI_CONFIG,
          status: 'error',
          error: error instanceof Error ? error.message : 'Failed to load UI config',
        })
      }
    }

    loadConfig()
  }, [isAuthenticated, auth0Loading, location.pathname, epicSessionRevision])

  /**
   * Reload a pinned client-config revision by its microapp document id and make
   * it the active config for every ``useUiConfig()`` consumer. Best-effort: any
   * failure (network, invalid payload, non-2xx) leaves the current config in
   * place and resolves ``false`` — the server still pins authoritatively, so a
   * failed SPA reload never blocks ordering.
   */
  const reloadByConfigId = useCallback(
    async (organizationId: string, configId: string): Promise<boolean> => {
      if (!organizationId?.trim() || !configId?.trim()) {
        return false
      }
      try {
        const client = ProviderAPIClient.getInstance()
        const response = await client.get(
          `/config/ui/${encodeURIComponent(organizationId.trim())}?configId=${encodeURIComponent(configId.trim())}`,
          { Accept: 'application/json' },
          true // withAuth: inject the flow-appropriate bearer token (Epic or Auth0)
        )
        if (!response.ok) {
          return false
        }
        const responseData = (await response.json()) as {
          ui_config?: unknown
          client_name?: string | null
        }
        const config = mergeClientNameFromApiEnvelope(
          assertUiConfig(responseData.ui_config),
          responseData.client_name
        )
        applyConfig(config)
        setState({ config, status: 'ready' })
        return true
      } catch (error) {
        Sentry.captureException(error, {
          tags: { operation: 'ui_config.reload_by_config_id' },
        })
        return false
      }
    },
    []
  )

  const contextValue = useMemo(() => ({ ...state, reloadByConfigId }), [state, reloadByConfigId])

  if (state.status === 'loading' && !isAuthBootstrapRoute(location.pathname)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-variant-gray dark:bg-gray-950">
        <div
          className="inline-block h-10 w-10 animate-spin rounded-full border-4 border-solid border-brand-600 dark:border-brand-500 border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
          role="status"
        >
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    )
  }

  return <UiConfigContext.Provider value={contextValue}>{children}</UiConfigContext.Provider>
}
